import styled from "@emotion/styled";
import React, { useEffect } from "react";
import AnimateHeight from "react-animate-height";
import PropTypes from "prop-types";

import BaseLink from "~/components/links/BaseLink";

import Image from "~/components/Image";
import ErrorBoundary from "~/components/ErrorBoundary";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { getProductUrl } from "~/utilities/products";

import { useCompareContext } from "~/context/CompareContext";

const Compare = styled.div`
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    overflow-x: hidden;
    z-index: 100;
`;

const Header = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-around;
    background-color: #c9c9c9;
    font-size: 12px;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
`;

const CompareCount = styled.p`
    color: #4674a4;
    text-transform: uppercase;
    font-weight: bold;
    margin: 0;
`;

const StopComparingButton = styled.button`
    color: #dd3444;
    cursor: pointer;
    border: none;
    background: none;
    text-transform: uppercase;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: inherit;
    font-weight: bold;
`;

const StopComparingIcon = styled.span`
    font-weight: bold;
    margin-right: 5px;
    font-size: 18px;
`;

const Container = styled.main`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.palette.darkBlue};
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        flex-direction: row;
    }
`;

const Products = styled.ul`
    display: grid;
    grid-template-columns: 1fr 1fr;
    background-color: white;
    font-size: 1.2rem;
    align-self: stretch;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        margin: 0 3rem;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        display: flex;
        justify-content: center;
        flex: 0 0 60%;
    }
`;

const Product = styled.li`
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: solid #c9c9c9 1px;
    border-top: solid #c9c9c9 1px;
    padding: 1rem;
    height: 120px;
    position: relative;
    &:last-of-type {
        border-right: solid #c9c9c9 1px;
    }
    & {
        animation: ${(props) => (props.isSelected ? "pulse" : "")} 1.5s;
    }
    @keyframes pulse {
        0% {
            background-color: #fff;
        }
        50% {
            background-color: lightblue;
        }
        100% {
            background-color: #fff;
        }
    }
    & span {
        font-size: 1.6rem;
        margin: 0 auto;
        display: inline-block;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        &:first-of-type {
            margin-left: auto;
        }
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        width: 22%;
        max-width: 200px;
        border-top: 0;
    }
`;

const ImageLinkContainer = styled.div`
    margin-top: 30px;
    p {
        text-transform: uppercase;
        font-weight: 400;
    }
`;

const ProductImage = styled(Image)`
    width: 30px;
    height: 50px;
    margin-right: 1rem;
`;

const ProductLink = styled(BaseLink)`
    text-decoration: none;
    text-transform: uppercase;
    color: ${({ theme }) => theme.palette.darkBlue};
    font-weight: bold;
    margin-bottom: 5px;
    display: inline-block;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: 5%;
    left: 85%;
    color: red;
    font-weight: bold;
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
`;

const CompareButtonContainer = styled.div`
    padding-top: 3rem;
    padding-bottom: 3rem;
    flex: 0 0 40%;
`;

const CompareButton = styled.button`
    font-size: 1.5rem;
    text-transform: uppercase;
    padding: 1.5rem;
    margin: 0 auto;
    background-color: #ffc000;
    color: ${({ theme }) => theme.palette.darkBlue};
    font-weight: bold;
    letter-spacing: 1px;
    display: inline-block;
`;

const ClearAllContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    padding: 1rem;
    left: 0;
    bottom: 0;
    width: 100%;
    padding-top: ${(props) => (props.isOpen ? "11rem" : "0")};
    padding-bottom: ${(props) => (props.isOpen ? "11rem" : "0")};
    font-size: 2.5rem;
    background-image: radial-gradient(
        ${({ theme }) => theme.palette.lightBlue},
        ${({ theme }) => theme.palette.darkBlue}
    );
    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        padding-top: ${(props) => (props.isOpen ? "6rem" : "0")};
        padding-bottom: ${(props) => (props.isOpen ? "6rem" : "0")};
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        padding-top: ${(props) => (props.isOpen ? "3.8rem" : "0")};
        padding-bottom: ${(props) => (props.isOpen ? "3.8rem" : "0")};
    }
`;

const ClearAllText = styled.p`
    color: white;
    letter-spacing: 1px;
    text-align: center;
    margin-bottom: 3rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        text-align: left;
        margin-bottom: 0;
        display: inline;
    }
`;

const ClearButtons = styled.div`
    text-align: center;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        text-align: left;
        display: inline;
    }
`;

const ClearAllNo = styled.button`
    background-color: ${({ theme }) => theme.palette.darkBlue};
    color: white;
    padding: 1.5rem 2.5rem;
    font-weight: bold;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    &:hover {
        background-color: #1174ba;
        transition: background-color 1s ease;
    }
`;

const ClearAllYes = styled.button`
    background-color: #ffc000;
    color: #005594;
    margin: 0 2rem;
    padding: 1.5rem 2.5rem;
    font-weight: bold;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    &:hover {
        background-color: #998548;
        transition: background-color 1s ease;
    }
`;

const ToggleButton = styled.button`
    background-color: #c9c9c9;
    color: ${({ theme }) => theme.palette.darkBlue};
    font-weight: bold;
    padding: 1rem 4rem;
    margin-left: auto;
    margin-right: 20%;
    display: block;
`;

const getButtonText = (selectedProducts) => {
    if (selectedProducts.length > 1) {
        return `Compare ${selectedProducts.length} Products`;
    }

    return "Select More Products To Compare";
};

const getToggleText = (show, size) => {
    if (show) {
        return size > 1 ? "Hide Products" : "Hide Product";
    } else {
        return size > 1 ? "Show Products" : "Show Product";
    }
};

const CompareTool = ({ selectedProducts = [], location }) => {
    const {
        clearAll,
        removeProduct,
        show,
        showClearAll,
        setShow,
        setShowClearAll,
        setUrlPath,
        goToComparePage,
        selectedCategory,
        clickedOnProductId,
    } = useCompareContext();

    useEffect(() => {
        setUrlPath(location.pathname);
    }, [location.pathname, setUrlPath]);

    const handleSelectedProducts = () => {
        const theSelectedProducts = selectedProducts.map((product) => {
            const url = getProductUrl(product);

            return (
                <Product
                    key={product.id}
                    isSelected={clickedOnProductId === product.id}
                >
                    <RemoveButton onClick={() => removeProduct(product.id)}>
                        &times;
                    </RemoveButton>

                    {product.featuredImage?.node ? (
                        <ProductImage
                            {...product.featuredImage.node}
                            objectFit="contain"
                        />
                    ) : null}

                    <ImageLinkContainer>
                        <ProductLink to={url}>
                            {sanitizeAndParseHTML(product.title)}
                        </ProductLink>
                        <p>{selectedCategory.name}</p>
                    </ImageLinkContainer>
                </Product>
            );
        });

        if (theSelectedProducts.length === 4) {
            return theSelectedProducts;
        }

        const formattedSelectedProducts = [...theSelectedProducts];

        for (let i = 1; i <= 4 - theSelectedProducts.length; i++) {
            formattedSelectedProducts.push(
                <Product key={i}>
                    <span>{i + theSelectedProducts.length}</span>
                </Product>,
            );
        }

        return formattedSelectedProducts;
    };

    return (
        <Compare>
            <ErrorBoundary>
                {selectedProducts.length > 0 && (
                    <ToggleButton onClick={() => setShow(!show)}>
                        {getToggleText(show, selectedProducts.length)}
                    </ToggleButton>
                )}
                <AnimateHeight
                    duration={500}
                    height={show && selectedProducts.length > 0 ? `auto` : 0}
                >
                    <Header>
                        <CompareCount>
                            Compared {selectedCategory.name} Products{" "}
                            <span>({selectedProducts.length})</span>
                        </CompareCount>
                        <StopComparingButton
                            onClick={() => setShowClearAll(true)}
                        >
                            <StopComparingIcon>&times;</StopComparingIcon> Close
                        </StopComparingButton>
                    </Header>
                    <Container>
                        <Products>{handleSelectedProducts()}</Products>
                        <CompareButtonContainer>
                            <CompareButton onClick={() => goToComparePage()}>
                                {getButtonText(selectedProducts)}
                            </CompareButton>
                        </CompareButtonContainer>
                    </Container>
                    <ClearAllContainer isOpen={showClearAll}>
                        <AnimateHeight
                            duration={500}
                            height={showClearAll ? `auto` : 0}
                        >
                            <ClearAllText>
                                Are you sure you want to stop comparing?
                            </ClearAllText>
                            <ClearButtons>
                                <ClearAllYes onClick={clearAll}>
                                    Yes
                                </ClearAllYes>
                                <ClearAllNo
                                    onClick={() => setShowClearAll(false)}
                                >
                                    No
                                </ClearAllNo>
                            </ClearButtons>
                        </AnimateHeight>
                    </ClearAllContainer>
                </AnimateHeight>
            </ErrorBoundary>
        </Compare>
    );
};

CompareTool.prototypes = {
    selectedProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default CompareTool;
